import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  useTheme,
  Typography
} from "@mui/material";
import { Edit, Cancel, Save } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { UpdateUserDetails } from "../../../redux/user/userSlice";
import { useDispatch } from "react-redux";
import { useNotification } from "../../shared/NotificationProvider";

const inputSx = {
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.08)",
  borderRadius: "4px",
  fontSize: "0.85rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.6)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fff",
  },
};

const readonlyFieldSx = {
  "& .MuiOutlinedInput-root": {
    pointerEvents: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.3)",
    },
  },
};

const labelSx = {
  color: "#fff",
  fontSize: "1rem",
};

const UserDetailsForm = ({ userInfo }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const notify = useNotification();
  const [editMode, setEditMode] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: userInfo?.name || "",
      email: userInfo?.email || "",
      phoneNumber: userInfo?.phoneNumber || "",
      twitter: userInfo?.socialMediaHandles?.twitter || "",
      instagram: userInfo?.socialMediaHandles?.instagram || "",
      linkedin: userInfo?.socialMediaHandles?.linkedin || ""
    }
  });

  const onSubmit = async (data) => {
    try {
      const res = await dispatch(
        UpdateUserDetails({
          name: data.name,
          email: data.email,
          phoneNumber: data.phoneNumber,
          socialMediaHandles: {
            twitter: data.twitter,
            instagram: data.instagram,
            linkedin: data.linkedin
          }
        })
      );
      notify[res.payload.status === "success" ? "success" : "error"](res.payload.message);
      setEditMode(false);
    } catch {
      notify.error("Failed to update user details");
    }
  };

  const fields = [
    { name: "name", label: "Name", required: true },
    { name: "email", label: "Email", required: true },
    { name: "phoneNumber", label: "Phone Number", required: false },
    { name: "twitter", label: "Twitter", required: false },
    { name: "instagram", label: "Instagram", required: false },
    { name: "linkedin", label: "LinkedIn", required: false }
  ];

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {!editMode ? (
          <Button
            startIcon={<Edit />}
            onClick={() => setEditMode(true)}
            sx={{
              color: "#fff",
              fontSize: "0.85rem",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Edit
          </Button>
        ) : (
          <Button
            startIcon={<Cancel />}
            onClick={() => {
              reset();
              setEditMode(false);
            }}
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontSize: "0.85rem",
              mr: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>

      {fields.map((field) => (
        <TextField
          key={field.name}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false
          })}
          label={field.label}
          variant="outlined"
          fullWidth
          margin="normal"
          sx={!editMode ? readonlyFieldSx : undefined}
          InputProps={{
            readOnly: !editMode,
            sx: inputSx
          }}
          InputLabelProps={{ sx: labelSx }}
          inputProps={{ tabIndex: editMode ? 0 : -1 }}
          error={!!errors[field.name]}
          helperText={errors[field.name]?.message}
          FormHelperTextProps={{
            sx: { color: "rgba(255,200,200,0.9)" }
          }}
        />
      ))}

      {editMode && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            sx={{
              fontSize: "0.85rem",
              backgroundColor: "#fff",
              color: "#001d3d",
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
          >
            Save Changes
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default UserDetailsForm;
